import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Mail, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { sendChatMessage, escalateToSupport } from '../lib/api';

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Welcome to LocalEdge 👋 How can we help you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showEscalation, setShowEscalation] = useState(false);
  const [escalationEmail, setEscalationEmail] = useState('');
  const [escalationSent, setEscalationSent] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await sendChatMessage(userMessage, sessionId);
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      
      // Check if response suggests escalation
      if (res.data.response.toLowerCase().includes('email') && 
          res.data.response.toLowerCase().includes('follow up')) {
        setShowEscalation(true);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble right now. Would you like to leave your email for our team to contact you?" 
      }]);
      setShowEscalation(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscalation = async () => {
    if (!escalationEmail.trim()) return;

    setIsLoading(true);
    try {
      const lastUserMessage = messages.filter(m => m.role === 'user').pop()?.content || 'Support request';
      await escalateToSupport(escalationEmail, lastUserMessage, sessionId);
      setEscalationSent(true);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Thanks! We'll reach out to ${escalationEmail} soon. Is there anything else I can help with?` 
      }]);
      setShowEscalation(false);
      setEscalationEmail('');
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I couldn't submit that. Please try again or email us directly at localedgehq@gmail.com" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 left-6 z-[9999] w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
        data-testid="chat-button"
        aria-label="Open chat support"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      <div 
        className={`fixed bottom-24 left-6 z-[9999] w-[360px] max-w-[calc(100vw-48px)] transition-all duration-300 ease-out ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}`}
        data-testid="chat-window"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[500px]">
          {/* Header */}
          <div className="bg-primary px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-primary-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-primary-foreground text-sm">LocalEdge Support</h3>
                <p className="text-xs text-primary-foreground/70">We typically reply instantly</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[350px] bg-background/50">
            {messages.map((msg, i) => (
              <div 
                key={i} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                    msg.role === 'user' 
                      ? 'bg-primary text-primary-foreground rounded-br-md' 
                      : 'bg-secondary text-foreground rounded-bl-md'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-secondary text-foreground px-4 py-2.5 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Escalation Form */}
          {showEscalation && !escalationSent && (
            <div className="px-4 py-3 bg-secondary/50 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Leave your email for our team:</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={escalationEmail}
                    onChange={(e) => setEscalationEmail(e.target.value)}
                    className="pl-9 h-9 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && handleEscalation()}
                    data-testid="escalation-email-input"
                  />
                </div>
                <Button 
                  size="sm" 
                  onClick={handleEscalation}
                  disabled={isLoading || !escalationEmail.trim()}
                  className="h-9"
                  data-testid="escalation-submit-button"
                >
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-border bg-card">
            <div className="flex gap-2">
              <Input
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                className="flex-1 h-10 bg-secondary/50"
                data-testid="chat-input"
              />
              <Button 
                onClick={handleSend} 
                disabled={isLoading || !inputValue.trim()}
                className="h-10 w-10 p-0"
                data-testid="chat-send"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
